import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { FEATURED_MEMBERS, NEWS, IMAGES } from '../mock';
import { useReveal } from '../hooks/useReveal';
import { getMembers, getNews } from '../lib/api';
import Footer from './Footer';

const IMG_POOL = [IMAGES.atv, IMAGES.cafe, IMAGES.gym, IMAGES.joinImg, IMAGES.heroC, IMAGES.heroB];

function Featured() {
  const scroller = useRef(null);
  const [ref, inView] = useReveal();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    getMembers({ status: 'Active' })
      .then((data) => {
        const feat = data.filter((m) => m.featured);
        const list = (feat.length ? feat : data).slice(0, 8);
        setMembers(list);
      })
      .catch(() => setMembers(FEATURED_MEMBERS));
  }, []);

  const scroll = (dir) => {
    if (scroller.current) scroller.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
  };

  return (
    <section className="bg-white py-24 md:py-28 overflow-hidden">
      <div ref={ref} className={`max-w-[1440px] mx-auto px-6 lg:px-10 reveal ${inView ? 'in-view' : ''}`}>
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-3">FEATURED MEMBERS</p>
            <h2 className="font-serif-ah text-[36px] md:text-[46px] text-[#2f2f2f] leading-none">
              Discover our featured businesses in Almaguin!
            </h2>
          </div>
          <div className="hidden md:flex gap-3">
            {[ChevronLeft, ChevronRight].map((Icon, i) => (
              <button
                key={i === 0 ? 'prev' : 'next'}
                onClick={() => scroll(i === 0 ? -1 : 1)}
                className="w-11 h-11 rounded-full border border-[#4a9cd4] text-[#4a9cd4] flex items-center justify-center hover:bg-[#4a9cd4] hover:text-white transition-colors"
                aria-label={i === 0 ? 'Previous' : 'Next'}
              >
                <Icon size={20} />
              </button>
            ))}
          </div>
        </div>

        <div ref={scroller} className="flex gap-6 overflow-x-auto pb-4 snap-x scroll-smooth" style={{ scrollbarWidth: 'none' }}>
          {members.map((m, i) => (
            <Link
              key={m.id}
              to={`/directory/${m.id}`}
              className="group relative shrink-0 w-[300px] md:w-[360px] h-[460px] rounded-sm overflow-hidden snap-start"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-110"
                style={{ backgroundImage: `url(${m.image || IMG_POOL[i % IMG_POOL.length]})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="font-sans-ah tracking-wide-ah text-[11px] text-white/80 uppercase mb-2">
                  {(m.categories && m.categories[0]) || m.category || 'Chamber Member'}
                </p>
                <h3 className="font-serif-ah text-white text-[28px] leading-tight mb-3">{m.name}</h3>
                <span className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-white opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  LEARN MORE <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsSection() {
  const [ref, inView] = useReveal();
  const [news, setNews] = useState([]);

  useEffect(() => {
    getNews()
      .then((d) => setNews(d.slice(0, 3)))
      .catch(() => setNews(NEWS));
  }, []);

  return (
    <section className="bg-[#f8f6f2] py-24 md:py-28">
      <div ref={ref} className={`max-w-[1440px] mx-auto px-6 lg:px-10 reveal ${inView ? 'in-view' : ''}`}>
        <div className="text-center mb-14">
          <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-3">HAPPENINGS</p>
          <h2 className="font-serif-ah text-[40px] md:text-[52px] text-[#2f2f2f] leading-none">News &amp; Events</h2>
          <p className="font-sans-ah text-[#666] text-[16px] font-300 mt-4">
            Discover everything happening in the Almaguin Highlands.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {news.map((n) => (
            <Link
              key={n.id}
              to={`/news/${n.id}`}
              className="group bg-white rounded-sm overflow-hidden shadow-[0_6px_30px_rgba(0,0,0,0.05)] hover:shadow-[0_14px_44px_rgba(0,0,0,0.12)] transition-shadow duration-500"
            >
              <div className="relative h-[230px] overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-105"
                  style={{ backgroundImage: `url(${n.image})` }}
                />
                <span className="absolute top-4 left-4 bg-[#4a9cd4] text-white font-sans-ah text-[10px] tracking-wide-ah px-3 py-1.5 rounded-sm">
                  {n.tag}
                </span>
              </div>
              <div className="p-7">
                <p className="font-sans-ah tracking-wide-ah text-[11px] text-[#999] uppercase mb-3">{n.date}</p>
                <h3 className="font-serif-ah text-[24px] text-[#2f2f2f] leading-snug mb-3 group-hover:text-[#4a9cd4] transition-colors">
                  {n.title}
                </h3>
                <p className="font-sans-ah text-[14px] text-[#666] font-300 leading-relaxed mb-4 line-clamp-3">{n.excerpt}</p>
                <span className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4]">
                  READ MORE <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/news" className="inline-block btn-ah-outline-dark font-sans-ah text-[13px] px-9 py-4 rounded-sm">
            VIEW ALL NEWS
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Featured_News() {
  return (
    <>
      <Featured />
      <NewsSection />
      <Footer />
    </>
  );
}
